import { Injectable } from '@angular/core';
import { NgForm, NgModel } from '@angular/forms';
import * as convertKeys from 'convert-keys';
import { Locale, LocaleBaseConfig } from '../model/locale.model';
import { ApiService } from './api.service';
import { BehaviorSubject } from 'rxjs';

@Injectable()
export class LocalesService {
  locales$: BehaviorSubject<Locale[]>;

  public static readonly LOCALES_ERROR_KEY = 'localesIncomplete';
  public static readonly REQUIRED_ALL_LOCALES = 'requiredAllLocales';

  constructor(private apiService: ApiService) {
    this.locales$ = new BehaviorSubject<Locale[]>([]);
  }

  async fetchLocales() {
    const locales = await this.apiService.get('locales');
    this.locales$.next(convertKeys.toCamel(locales));
  }

  checkForLocale(locale_key: string, locales: Locale[]) {
    return locales.find(locale => locale.localeName === locale_key);
  }

  ensureLocaleSupported(
    config: LocaleBaseConfig,
    availableLocales: Locale[] | undefined,
    localeName: string,
  ): void {
    if (!availableLocales || availableLocales.length === 0) return;
    const requiredLocale = availableLocales.find(locale => locale.localeName === localeName);
    const isAlreadySupported = config.supportedLocales?.some(locale => locale.localeName === localeName);
    if (requiredLocale && !isAlreadySupported) {
      config.supportedLocales = [requiredLocale, ...(config.supportedLocales || [])];
    }
  }

  validateAllSupportedLocalesFilled(
    config: LocaleBaseConfig,
    valueMap: Record<string, any> | undefined,
    ctx: { model?: NgModel; form: NgForm; wrapperId?: string; groupId?: string; field?: string },
  ): void {
    setTimeout(() => {
      // Touch outer wrapper control used by multi-locale-input
      const { model: ngModel, form, wrapperId, groupId, field } = ctx;
      if (wrapperId) {
        const wrapper = form.form.controls?.[wrapperId] as any;
        wrapper?.markAsDirty?.();
        wrapper?.markAsTouched?.();
      }
      if (groupId && field) {
        const group: any = form.form.controls?.[groupId];
        const child = group?.controls?.[field];
        child?.markAsDirty?.();
        child?.markAsTouched?.();
      }

      const control = ngModel?.control as any;
      if (!control) return;

      const supported = config?.supportedLocales || [];
      const allFilled = supported.length
        ? supported.every((loc: any) => {
            const key = loc?.localeName;
            const val = key ? valueMap?.[key] : undefined;
            if (typeof val === 'string') return val.trim().length > 0;
            return Boolean(val);
          })
        : true;

      const existingErrors = control.errors || {};
      if (allFilled) {
        const { [LocalesService.REQUIRED_ALL_LOCALES]: _drop, ...rest } = existingErrors as any;
        const newErrors = Object.keys(rest).length ? rest : null;
        control.setErrors(newErrors);
      } else {
        control.setErrors({ ...existingErrors, [LocalesService.REQUIRED_ALL_LOCALES]: true });
      }

      control.markAsTouched();
      control.markAsDirty();
      control.updateValueAndValidity({ onlySelf: false, emitEvent: true });

      // Reflect at form level with a namespaced error per-field to avoid sticky 'invalid'
      const idKey = wrapperId || (groupId && field ? `${groupId}.${field}` : 'locales');
      const formErrors = { ...(form.form.errors || {}) } as any;
      const localesIncomplete = { ...(formErrors[LocalesService.LOCALES_ERROR_KEY] || {}) } as Record<string, boolean>;

      if (!allFilled) {
        localesIncomplete[idKey] = true;
        form.form.setErrors({ ...formErrors, [LocalesService.LOCALES_ERROR_KEY]: localesIncomplete });
      } else {
        if (localesIncomplete[idKey]) {
          delete localesIncomplete[idKey];
        }
        const hasLocalesErrors = Object.keys(localesIncomplete).length > 0;
        const { [LocalesService.LOCALES_ERROR_KEY]: _old, invalid, ...rest } = formErrors; // also strip legacy 'invalid'
        const nextErrors = hasLocalesErrors ? { ...rest, localesIncomplete } : { ...rest };
        form.form.setErrors(Object.keys(nextErrors).length ? nextErrors : null);
      }

      form.form.updateValueAndValidity({ onlySelf: false, emitEvent: true });
    });
  }

  clearLocalesError(
    ngModel: NgModel | undefined,
    form: NgForm,
    ctx: { wrapperId?: string; groupId?: string; field?: string },
  ): void {
    const control = ngModel?.control as any;
    if (control) {
      control.setErrors(null);
      control.updateValueAndValidity({ onlySelf: false, emitEvent: true });
    }
    const { wrapperId, groupId, field } = ctx;
    if (wrapperId) {
      const wrapper: any = form.form.controls?.[wrapperId];
      wrapper?.setErrors?.(null);
      wrapper?.updateValueAndValidity?.({ onlySelf: false, emitEvent: true });
    }
    if (groupId && field) {
      const group: any = form.form.controls?.[groupId];
      const child: any = group?.controls?.[field];
      child?.setErrors?.(null);
      child?.updateValueAndValidity?.({ onlySelf: false, emitEvent: true });
    }
    const idKey = wrapperId || (groupId && field ? `${groupId}.${field}` : 'locales');
    const formErrors = { ...(form.form.errors || {}) } as any;
    if (formErrors[LocalesService.LOCALES_ERROR_KEY]) {
      delete formErrors[LocalesService.LOCALES_ERROR_KEY][idKey];
      if (Object.keys(formErrors[LocalesService.LOCALES_ERROR_KEY]).length === 0) {
        delete formErrors[LocalesService.LOCALES_ERROR_KEY];
      }
    }
    form.form.setErrors(Object.keys(formErrors).length ? formErrors : null);
    form.form.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }
}
